import React, { useState, useEffect } from 'react';
import { jobsAPI } from '../../utils/api';
import AdminHeader from './AdminHeader';
import JobEditor from './JobEditor';
import ShareJobDropdown from '../shared/ShareJobDropdown';
import './AdminDashboard.css';
import './JobsManagement.css';

/**
 * JobsManagement Component
 *
 * Allows admins and recruiters to:
 * - View all job postings
 * - Edit job details including slug
 * - Publish/close jobs
 * - Delete jobs
 */
const JobsManagement = ({ onNavigate, currentView }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingJob, setEditingJob] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateEditor, setShowCreateEditor] = useState(false);
  const [showEditEditorJobId, setShowEditEditorJobId] = useState(null);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await jobsAPI.getAll();
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('[JobsManagement] Error loading jobs:', err);
      setError(err.response?.data?.error || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job._id);
    setEditForm({
      title: job.title,
      slug: job.slug,
      company: job.company,
      location: job.location,
      workMode: job.workMode,
      employmentType: job.employmentType,
      experienceLevel: job.experienceLevel,
      description: job.description,
      status: job.status,
      isPublic: job.isPublic
    });
  };

  const handleCancelEdit = () => {
    setEditingJob(null);
    setEditForm({});
  };

  const handleCreateNew = () => {
    setShowCreateEditor(true);
  };

  const handleCloseCreateEditor = () => {
    setShowCreateEditor(false);
  };

  const handleCreateSave = () => {
    setSuccess('Job created successfully!');
    setTimeout(() => setSuccess(''), 3000);
    setShowCreateEditor(false);
    loadJobs();
  };

  const handleCloseEditEditor = () => {
    setShowEditEditorJobId(null);
  };

  const handleEditSave = () => {
    setSuccess('Job updated successfully!');
    setTimeout(() => setSuccess(''), 3000);
    setShowEditEditorJobId(null);
    loadJobs();
  };

  const handleSaveEdit = async (jobId) => {
    try {
      setError('');
      await jobsAPI.update(jobId, editForm);
      setSuccess('Job updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setEditingJob(null);
      setEditForm({});
      loadJobs();
    } catch (err) {
      console.error('[JobsManagement] Error updating job:', err);
      setError(err.response?.data?.error || 'Failed to update job');
    }
  };

  const handlePublish = async (jobId) => {
    if (!window.confirm('Publish this job posting? It will be visible to the public.')) {
      return;
    }

    try {
      await jobsAPI.publish(jobId);
      setSuccess('Job published successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadJobs();
    } catch (err) {
      console.error('[JobsManagement] Error publishing job:', err);
      setError(err.response?.data?.error || 'Failed to publish job');
    }
  };

  const handleClose = async (jobId) => {
    if (!window.confirm('Close this job posting? It will stop accepting applications.')) {
      return;
    }

    try {
      await jobsAPI.close(jobId);
      setSuccess('Job closed successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadJobs();
    } catch (err) {
      console.error('[JobsManagement] Error closing job:', err);
      setError(err.response?.data?.error || 'Failed to close job');
    }
  };

  const handleDelete = async (jobId, title) => {
    if (!window.confirm(`Delete job "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await jobsAPI.delete(jobId);
      setSuccess('Job deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadJobs();
    } catch (err) {
      console.error('[JobsManagement] Error deleting job:', err);
      setError(err.response?.data?.error || 'Failed to delete job');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { backgroundColor: '#6c757d', color: 'white' },
      active: { backgroundColor: '#28a745', color: 'white' },
      closed: { backgroundColor: '#dc3545', color: 'white' },
      paused: { backgroundColor: '#ffc107', color: 'black' }
    };

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        ...(styles[status] || styles.draft)
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <AdminHeader currentView={currentView} onNavigate={onNavigate} />
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <AdminHeader currentView={currentView} onNavigate={onNavigate} />

      <main className="jobs-management-container">
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2>Jobs Management</h2>
            <p style={{ color: '#666', marginTop: '0.5rem' }}>
              Manage job postings, edit slugs, and control visibility
            </p>
          </div>
          <button
            className="action-btn publish-btn"
            onClick={handleCreateNew}
            style={{ padding: '0.75rem 1.5rem' }}
            title="Create new job posting"
          >
            + Create New Job
          </button>
        </div>

        {error && (
          <div className="error-banner" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px' }}>
            {success}
          </div>
        )}

        <div className="jobs-table-wrapper">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Location</th>
                <th>Status</th>
                <th>Public</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    No jobs found
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job._id}>
                    {editingJob === job._id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            className="edit-input"
                            value={editForm.title}
                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="edit-input job-slug"
                            value={editForm.slug}
                            onChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="edit-input"
                            value={editForm.location}
                            onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                          />
                        </td>
                        <td>
                          <select
                            className="edit-select"
                            value={editForm.status}
                            onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                          >
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="closed">Closed</option>
                            <option value="paused">Paused</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            className="public-checkbox"
                            checked={editForm.isPublic}
                            onChange={(e) => setEditForm({ ...editForm, isPublic: e.target.checked })}
                          />
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="action-btn save-btn"
                              onClick={() => handleSaveEdit(job._id)}
                            >
                              Save
                            </button>
                            <button
                              className="action-btn cancel-btn"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{job.title}</td>
                        <td className="job-slug">{job.slug}</td>
                        <td>{job.location}</td>
                        <td>{getStatusBadge(job.status)}</td>
                        <td style={{ textAlign: 'center' }}>{job.isPublic ? '✓' : '✗'}</td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <ShareJobDropdown job={job} compact />
                            <button
                              className="action-btn edit-btn"
                              onClick={() => setShowEditEditorJobId(job._id)}
                            >
                              Edit
                            </button>
                            {job.status !== 'active' && (
                              <button
                                className="action-btn publish-btn"
                                onClick={() => handlePublish(job._id)}
                              >
                                Publish
                              </button>
                            )}
                            {job.status === 'active' && (
                              <button
                                className="action-btn close-btn"
                                onClick={() => handleClose(job._id)}
                              >
                                Close
                              </button>
                            )}
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDelete(job._id, job.title)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {showCreateEditor && (
        <JobEditor
          jobId={null}
          onClose={handleCloseCreateEditor}
          onSave={handleCreateSave}
        />
      )}

      {showEditEditorJobId && (
        <JobEditor
          jobId={showEditEditorJobId}
          onClose={handleCloseEditEditor}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default JobsManagement;
